import { ref, readonly, type Ref } from 'vue'

/**
 * Public game configuration consumed by the betting UI, sourced from the
 * `/crash-config` edge function. USD caps come from the server; `minBet`
 * is a XAUT amount that mirrors the server-side engine constant.
 *
 * Lifecycle:
 *   1. On first `useGameConfig()` call we hydrate from localStorage (sync,
 *      fast) and immediately kick off a background `refresh()` to revalidate.
 *   2. On successful fetch we update the reactive state AND localStorage.
 *   3. On fetch failure we keep whatever is currently in `config` (last good
 *      response from localStorage, or the hardcoded fallback if first visit).
 *
 * The composable is a module-scope singleton — every component that calls it
 * gets the same `config` ref, so a single fetch updates everyone.
 *
 * Trimmed 2026-05-25: `houseEdge` / `maxBetPercent` / `maxRoundPayoutUsd` /
 * `bettingDurationMs` / `cooldownDurationMs` were removed from the public
 * `/crash-config` response. Admin display moved to `/admin-crash-config`.
 */
/** Third-party integration kill switches carried on /crash-config so the web
 * boot path doesn't need a separate fetch just to gate Clarity. Optional on
 * the wire — older backends predate the field and we fail-open. */
export interface GameConfigAnalytics {
  clarityEnabled: boolean
}

/** In-game connection-lost banner knobs. Optional on the wire (older backends
 *  predate the field) so we fail-open to enabled=true / 1000ms below. */
export interface GameConfigConnectionBanner {
  enabled: boolean
  delayMs: number
}

/** Marketing / third-party pixel kill switches. Carried on /crash-config so the
 *  web boot path can gate the VK retargeting pixel without a separate fetch.
 *  `retargetingEnabled` is the admin kill-switch AND the flag the
 *  script-integrity monitor flips to false on drift. Optional on the wire
 *  (older backends predate it) so we fail-open to enabled below. */
export interface GameConfigMarketing {
  retargetingEnabled: boolean
}

export interface GameConfig {
  minBet: number
  maxBetAbsoluteUsd: number
  maxWinCapUsd: number
  analytics: GameConfigAnalytics
  connectionBanner: GameConfigConnectionBanner
  marketing: GameConfigMarketing
}

const STORAGE_KEY = 'aurumrun_game_config'

const FALLBACK: GameConfig = {
  minBet: 0.00001,
  maxBetAbsoluteUsd: 100,
  maxWinCapUsd: 1000,
  // Fail-open default. If the backend response or localStorage is missing the
  // analytics block, callers (clarity.client.ts) see Clarity enabled, which
  // matches the pre-kill-switch behavior.
  analytics: { clarityEnabled: true },
  // Fail-open default. A missing connectionBanner block (older backend / cached
  // pre-feature blob) means the banner shows with the original 1s debounce.
  connectionBanner: { enabled: true, delayMs: 1000 },
  // Fail-open default. A missing marketing block (older backend / cached
  // pre-feature blob) means the retargeting pixel gate stays open, matching the
  // pre-kill-switch behavior. The admin kill-switch / monitor auto-disable flip
  // this to false, reaching the client within ~60s + the next page load.
  marketing: { retargetingEnabled: true },
}

function isValidConfig(value: unknown): value is GameConfig {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  return (
    typeof v.minBet === 'number' &&
    typeof v.maxBetAbsoluteUsd === 'number' &&
    typeof v.maxWinCapUsd === 'number'
  )
  // analytics is intentionally NOT checked — it's optional on the wire so an
  // older backend deploy doesn't fail validation. Merge with FALLBACK below
  // backfills the field when missing.
}

// Project to known keys only — defends against stale localStorage shapes
// from older app versions leaking removed fields (e.g. `houseEdge`) into
// reactive state where they'd type-check as `any` and look fresh. Hand-
// merges `analytics` so a partial nested object from a pre-kill-switch
// cached blob still gets each individual flag backfilled from FALLBACK.
function projectKnownKeys(input: GameConfig): GameConfig {
  return {
    minBet: input.minBet,
    maxBetAbsoluteUsd: input.maxBetAbsoluteUsd,
    maxWinCapUsd: input.maxWinCapUsd,
    analytics: { ...FALLBACK.analytics, ...(input.analytics ?? {}) },
    connectionBanner: { ...FALLBACK.connectionBanner, ...(input.connectionBanner ?? {}) },
    marketing: { ...FALLBACK.marketing, ...(input.marketing ?? {}) },
  }
}

function readFromStorage(): GameConfig | null {
  if (typeof window === 'undefined') return null
  // The try/catch must wrap the `window.localStorage` access itself, not only
  // JSON.parse. Observed in prod: the SSR runtime can expose a truthy-but-
  // partial `window` that passes the `typeof` guard above yet throws on the
  // `.localStorage` read. Catching here returns the FALLBACK and keeps SSR/
  // hydration identical to a normal "no window" environment.
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<GameConfig>
    if (!isValidConfig(parsed)) return null
    return projectKnownKeys({ ...FALLBACK, ...parsed })
  } catch {
    return null
  }
}

function writeToStorage(cfg: GameConfig): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg))
  } catch {
    // Quota / disabled storage — non-fatal, in-memory state still works.
    // Also covers the prod SSR partial-`window` case where the `typeof`
    // guard passes but the `.localStorage` access throws.
  }
}

// Runs at module-eval, which happens during SSR. Safe because
// `readFromStorage()` is fully wrapped in try/catch (including the
// `window.localStorage` access) and falls back to FALLBACK — it cannot throw
// even against the prod SSR partial-`window` shim.
const config = ref<GameConfig>(readFromStorage() ?? FALLBACK)
let refreshPromise: Promise<void> | null = null
let initialFetchKicked = false

async function refresh(): Promise<void> {
  if (refreshPromise) return refreshPromise
  refreshPromise = (async () => {
    try {
      const runtime = useRuntimeConfig()
      const data = await $fetch<GameConfig>(`${runtime.public.apiBase}/crash-config`)
      if (isValidConfig(data)) {
        // Merge with FALLBACK so a regression that drops a field on the
        // backend doesn't leave consumers reading `undefined`. Project to
        // known keys so a server that ever drifts back to returning
        // removed business fields (houseEdge etc.) doesn't smuggle them
        // into in-memory state or localStorage. `projectKnownKeys` also
        // hand-merges the nested `analytics` block so partial responses
        // keep the kill-switch fail-open guarantee per individual flag.
        const merged = projectKnownKeys({ ...FALLBACK, ...data })
        config.value = merged
        writeToStorage(merged)
      }
    } catch (err) {
      console.error('[useGameConfig] /crash-config fetch failed; using cached/fallback', err)
    } finally {
      refreshPromise = null
    }
  })()
  return refreshPromise
}

export interface UseGameConfigReturn {
  config: Readonly<Ref<GameConfig>>
  refresh: () => Promise<void>
}

export function useGameConfig(): UseGameConfigReturn {
  if (!initialFetchKicked && typeof window !== 'undefined') {
    initialFetchKicked = true
    void refresh()
  }
  return {
    config: readonly(config) as Readonly<Ref<GameConfig>>,
    refresh,
  }
}
